const CONTROL_KEYS = new Set([
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'KeyW', 'KeyS', 'KeyA', 'KeyD', 'KeyQ', 'KeyE',
  'ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight',
  'Space', 'KeyF'
]);

export class InputController {
  constructor(root, callbacks = {}) {
    this.root = root;
    this.callbacks = callbacks;
    this.keys = new Set();
    this.touch = { pitch: 0, roll: 0, yaw: 0, throttle: 0, gun: false, missile: false };
    this.joystickPointer = null;
    this.bindKeyboard();
    this.bindTouch();
  }

  bindKeyboard() {
    window.addEventListener('keydown', (event) => {
      if (CONTROL_KEYS.has(event.code)) event.preventDefault();
      this.keys.add(event.code);
      if (event.repeat) return;
      if (event.code === 'KeyC') this.callbacks.onCamera?.();
      if (event.code === 'KeyP' || event.code === 'Escape') this.callbacks.onPause?.();
      if (event.code === 'KeyR') this.callbacks.onRestart?.();
      if (event.code === 'Enter') this.callbacks.onStart?.();
    });
    window.addEventListener('keyup', (event) => this.keys.delete(event.code));
    window.addEventListener('blur', () => this.keys.clear());
    window.addEventListener('wheel', (event) => {
      this.touch.throttle = Math.sign(-event.deltaY);
      window.clearTimeout(this.wheelTimer);
      this.wheelTimer = window.setTimeout(() => { this.touch.throttle = 0; }, 90);
    }, { passive: true });
  }

  bindTouch() {
    const joystick = this.root.querySelector('[data-joystick]');
    const knob = this.root.querySelector('[data-joystick-knob]');
    if (joystick) {
      const updateJoystick = (event) => {
        const bounds = joystick.getBoundingClientRect();
        const radius = bounds.width * 0.34;
        let x = event.clientX - (bounds.left + bounds.width / 2);
        let y = event.clientY - (bounds.top + bounds.height / 2);
        const length = Math.hypot(x, y);
        if (length > radius) {
          x = x / length * radius;
          y = y / length * radius;
        }
        knob.style.transform = `translate(${x}px, ${y}px)`;
        this.touch.roll = x / radius;
        this.touch.pitch = y / radius;
      };
      const release = (event) => {
        if (event.pointerId !== this.joystickPointer) return;
        this.joystickPointer = null;
        this.touch.pitch = 0;
        this.touch.roll = 0;
        knob.style.transform = 'translate(0, 0)';
        joystick.releasePointerCapture?.(event.pointerId);
      };
      joystick.addEventListener('pointerdown', (event) => {
        this.joystickPointer = event.pointerId;
        joystick.setPointerCapture(event.pointerId);
        updateJoystick(event);
      });
      joystick.addEventListener('pointermove', (event) => {
        if (event.pointerId === this.joystickPointer) updateJoystick(event);
      });
      joystick.addEventListener('pointerup', release);
      joystick.addEventListener('pointercancel', release);
    }

    this.root.querySelectorAll('[data-hold]').forEach((button) => {
      const [control, amount] = button.dataset.hold.split(':');
      const press = (event) => {
        event.preventDefault();
        button.setPointerCapture(event.pointerId);
        this.touch[control] = Number(amount);
        button.classList.add('is-held');
      };
      const release = () => {
        this.touch[control] = 0;
        button.classList.remove('is-held');
      };
      button.addEventListener('pointerdown', press);
      button.addEventListener('pointerup', release);
      button.addEventListener('pointercancel', release);
    });

    this.root.querySelector('[data-camera]')?.addEventListener('click', () => this.callbacks.onCamera?.());
    this.root.querySelector('[data-pause]')?.addEventListener('click', () => this.callbacks.onPause?.());
    this.root.querySelector('[data-fire-gun]')?.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.touch.gun = true;
      event.currentTarget.classList.add('is-held');
    });
    const releaseGun = () => {
      this.touch.gun = false;
      this.root.querySelector('[data-fire-gun]')?.classList.remove('is-held');
    };
    this.root.querySelector('[data-fire-gun]')?.addEventListener('pointerup', releaseGun);
    this.root.querySelector('[data-fire-gun]')?.addEventListener('pointercancel', releaseGun);
    this.root.querySelector('[data-fire-missile]')?.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.touch.missile = true;
      window.setTimeout(() => { this.touch.missile = false; }, 120);
    });
  }

  getControls() {
    const keyPitch = (this.keys.has('KeyS') || this.keys.has('ArrowDown') ? 1 : 0) -
      (this.keys.has('KeyW') || this.keys.has('ArrowUp') ? 1 : 0);
    const keyRoll = (this.keys.has('KeyD') || this.keys.has('ArrowRight') ? 1 : 0) -
      (this.keys.has('KeyA') || this.keys.has('ArrowLeft') ? 1 : 0);
    const keyYaw = (this.keys.has('KeyE') ? 1 : 0) - (this.keys.has('KeyQ') ? 1 : 0);
    const keyThrottle = (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') ? 1 : 0) -
      (this.keys.has('ControlLeft') || this.keys.has('ControlRight') ? 1 : 0);
    return {
      pitch: Math.max(-1, Math.min(1, keyPitch || this.touch.pitch)),
      roll: Math.max(-1, Math.min(1, keyRoll || this.touch.roll)),
      yaw: Math.max(-1, Math.min(1, keyYaw || this.touch.yaw)),
      throttle: Math.max(-1, Math.min(1, keyThrottle || this.touch.throttle)),
      gun: this.keys.has('Space') || this.touch.gun,
      missile: this.keys.has('KeyF') || this.touch.missile
    };
  }
}
